import React from 'react';
import { Page } from '/imports/ui/Page';
import { PageHeading } from '/imports/ui/PageHeading';
import { PageBody } from '/imports/ui/PageBody';
import { useUser, useMethod } from '/imports/hooks';
import { NotPermittedPage } from '/imports/ui/NotPermittedPage';
import { LoadingFlex } from '/imports/ui/LoadingFlex';
import { getApiDocs, ApiRouteDoc } from '/imports/api/apiDocs';
import { Badge, BadgeTone } from '/imports/ui/Badge';

const h4Class = 'mt-3 mb-1 text-[.8rem] tracking-[.03em] text-fg-3 uppercase';
const preClass = 'overflow-x-auto rounded-md border border-soft bg-page-alt px-[.8rem] py-[.6rem] font-mono text-[.85rem]';
// Method chips share the Badge tone vocabulary rather than a second, Material
// palette. GET reads as safe, POST as informational, PATCH as caution, DELETE
// as destructive - the same meanings those tones carry everywhere else.
const methodTone: { [k: string]: BadgeTone } = {
  get: 'green',
  post: 'blue',
  patch: 'amber',
  delete: 'red',
};

const Kv = ({ obj }: { obj: { [k: string]: string } }) => (
  <table className="kv border-collapse text-[.9rem] [&_td]:border [&_td]:border-soft [&_td]:px-[.6rem] [&_td]:py-1 [&_td.k]:font-mono [&_td.k]:text-link"><tbody>
    {Object.entries(obj).map(([k, v]) => <tr key={k}><td className="k">{k}</td><td>{v}</td></tr>)}
  </tbody></table>
);

const RouteCard = ({ r }: { r: ApiRouteDoc }) => {
  const doc = r.doc || {};
  return (
    <div className="route my-3 rounded-card border border-soft bg-panel px-4 py-3">
      <div className="line flex items-center gap-[.6rem]">
        <Badge tone={methodTone[r.method.toLowerCase()] || 'neutral'}>{r.method}</Badge>
        <span className="path font-mono text-[.95rem]">/api{r.path}</span>
      </div>
      {doc.summary && <div className="summary mt-[.4rem] font-semibold">{doc.summary}</div>}
      {doc.description && <p className="desc my-1 text-fg-2">{doc.description}</p>}
      {doc.params && Object.keys(doc.params).length > 0 && <><h4 className={h4Class}>Path params</h4><Kv obj={doc.params} /></>}
      {doc.query && Object.keys(doc.query).length > 0 && <><h4 className={h4Class}>Query params</h4><Kv obj={doc.query} /></>}
      {r.body !== undefined && <><h4 className={h4Class}>Request body</h4><pre className={preClass}>{JSON.stringify(r.body, null, 2)}</pre></>}
      {doc.response !== undefined && <><h4 className={h4Class}>Example response</h4><pre className={preClass}>{JSON.stringify(doc.response, null, 2)}</pre></>}
    </div>
  );
};

export const ApiDocsPage = () => {
  const user = useUser();
  if (!user?.isSuperUser) return <NotPermittedPage />;
  const [routes, loading] = useMethod(getApiDocs, undefined, []);
  if (loading) return <LoadingFlex />;
  const groups: { [tag: string]: ApiRouteDoc[] } = {};
  for (const r of (routes || [])) {
    const tag = r.doc?.tags?.[0] || 'Other';
    (groups[tag] ||= []).push(r);
  }
  return (
    <Page className="page">
      <PageHeading left={<h1>API</h1>} />
      <PageBody>
        {Object.keys(groups).sort().map((tag) => (
          <section key={tag}>
            <h2 className="mt-8 border-b-2 border-soft pb-1">{tag}</h2>
            {groups[tag]
              .sort((a, b) => a.path.localeCompare(b.path) || a.method.localeCompare(b.method))
              .map((r, i) => <RouteCard key={`${r.method}-${r.path}-${i}`} r={r} />)}
          </section>
        ))}
      </PageBody>
    </Page>
  );
};
